import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/ui/use-toast";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import UnifiedPassBuilder from "@/components/wallet/UnifiedPassBuilder";
import { 
  Smartphone, 
  ArrowLeft, 
  Palette, 
  Settings, 
  Download,
  QrCode,
  Users,
  Star,
  Gift,
  Zap
} from "lucide-react";

interface Business {
  id: string;
  name: string;
  description: string | null;
  address: string;
  website: string | null;
  phone: string | null;
}

interface LoyaltyProgram {
  id: string;
  name: string;
  description: string;
  program_type: 'points' | 'stamps' | 'visits';
  reward_description: string;
  is_active: boolean;
  points_per_dollar?: number;
  stamps_required?: number;
  visits_required?: number;
  created_at: string;
}

const PassBuilderPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('businessId');
  const programId = searchParams.get('programId');
  
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [loyaltyPrograms, setLoyaltyPrograms] = useState<LoyaltyProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (!user) {
      toast.error("Authentication Required", {
        description: "You must be logged in to access the pass builder."
      });
      navigate("/sign-in");
      return;
    }

    fetchBusinessData();
  }, [user, navigate]);

  useEffect(() => {
    // Auto-select business if provided in URL params
    if (businessId && businesses.length > 0) {
      const business = businesses.find(b => b.id === businessId);
      if (business) {
        setSelectedBusiness(business);
        fetchLoyaltyPrograms(businessId);
      }
    }
  }, [businessId, businesses]);

  const fetchBusinessData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch user's businesses
      const { data: businessData, error: businessError } = await supabase
        .from("businesses")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (businessError) throw businessError;
      
      setBusinesses(businessData || []);

      // Auto-select first business if no specific business is requested
      if (!businessId && businessData && businessData.length > 0) {
        const firstBusiness = businessData[0];
        setSelectedBusiness(firstBusiness);
        fetchLoyaltyPrograms(firstBusiness.id);
      }

    } catch (error) {
      console.error("Error fetching business data:", error);
      toast.error("Error", {
        description: "Failed to load business data. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLoyaltyPrograms = async (businessId: string) => {
    try {
      const { data: programsData, error: programsError } = await supabase
        .from("loyalty_programs")
        .select("*")
        .eq("business_id", businessId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (programsError) throw programsError;
      
      setLoyaltyPrograms(programsData || []);

    } catch (error) {
      console.error("Error fetching loyalty programs:", error);
      toast.error("Error", {
        description: "Failed to load loyalty programs. Please try again."
      });
    }
  };

  const handleBusinessChange = (business: Business) => {
    setSelectedBusiness(business);
    fetchLoyaltyPrograms(business.id);
    setActiveTab("builder");
  };

  const handlePassGenerated = (passData: any) => {
    toast.success("Pass Generated Successfully!", {
      description: "Your digital wallet pass has been created and is ready for distribution."
    });
    
    // Optionally redirect to pass management or keep user here
    console.log("Generated pass data:", passData);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-12 flex justify-center items-center">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p>Loading pass builder...</p>
          </div>
        </div>
      </div>
    );
  }

  if (businesses.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <Smartphone className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h1 className="text-2xl font-bold mb-4">No Businesses Found</h1>
            <p className="mb-6 text-gray-600">
              You need to create a business before you can build digital wallet passes.
            </p>
            <div className="space-x-4">
              <Button onClick={() => navigate("/create-business")}>
                Create Your First Business
              </Button>
              <Button variant="outline" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Smartphone className="w-8 h-8" />
                Pass Builder
              </h1>
              <p className="text-gray-600 mt-1">Create digital wallet passes for your loyalty programs</p>
            </div>
            {selectedBusiness && (
              <Badge variant="outline" className="text-lg px-3 py-1">
                {selectedBusiness.name}
              </Badge>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="builder" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Builder
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Preview
            </TabsTrigger>
            <TabsTrigger value="distribute" className="flex items-center gap-2">
              <QrCode className="w-4 h-4" />
              Distribute
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Select Business
                  </CardTitle>
                  <CardDescription>
                    Choose which business to create passes for
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {businesses.map((business) => (
                      <div
                        key={business.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          selectedBusiness?.id === business.id
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        onClick={() => handleBusinessChange(business)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold">{business.name}</h3>
                            <p className="text-sm text-gray-600">{business.address}</p>
                          </div>
                          {selectedBusiness?.id === business.id && (
                            <Badge>Selected</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5" />
                    Loyalty Programs
                  </CardTitle>
                  <CardDescription>
                    Available programs for {selectedBusiness?.name || "your business"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loyaltyPrograms.length === 0 ? (
                    <div className="text-center py-6">
                      <Star className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-gray-500 mb-4">No loyalty programs found</p>
                      <Button
                        variant="outline"
                        onClick={() => navigate("/features/loyalty")}
                      >
                        Create Loyalty Program
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {loyaltyPrograms.map((program) => (
                        <div key={program.id} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold">{program.name}</h3>
                              <p className="text-sm text-gray-600">{program.description}</p>
                              <Badge className="mt-2">
                                {program.program_type}
                              </Badge>
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => setActiveTab("builder")}
                            >
                              Build Pass
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {selectedBusiness && loyaltyPrograms.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Quick Start
                  </CardTitle>
                  <CardDescription>
                    Ready to create your first digital wallet pass?
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div>
                      <p className="font-medium">
                        You have {loyaltyPrograms.length} loyalty program{loyaltyPrograms.length !== 1 ? 's' : ''} ready
                      </p>
                      <p className="text-sm text-gray-600">
                        Create Apple Wallet and Google Pay passes for your customers
                      </p>
                    </div>
                    <Button
                      onClick={() => setActiveTab("builder")}
                      className="flex items-center gap-2"
                    >
                      <Palette className="w-4 h-4" />
                      Start Building
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="builder" className="space-y-6">
            {selectedBusiness && loyaltyPrograms.length > 0 ? (
              <UnifiedPassBuilder
                businessId={selectedBusiness.id}
                loyaltyPrograms={loyaltyPrograms}
                selectedProgramId={programId || undefined}
                onPassGenerated={handlePassGenerated}
              />
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <Palette className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Programs Available</h3>
                  <p className="text-gray-600 mb-4">
                    You need to select a business and create a loyalty program first.
                  </p>
                  <div className="space-x-4">
                    <Button onClick={() => setActiveTab("overview")}>
                      Select Business
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/features/loyalty")}
                    >
                      Create Program
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="preview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  Pass Preview
                </CardTitle>
                <CardDescription>
                  See how your passes will look on different devices
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Smartphone className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">
                    Build a pass first to see the preview
                  </p>
                  <Button onClick={() => setActiveTab("builder")}>
                    Go to Builder
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="distribute" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Distribution Options
                </CardTitle>
                <CardDescription>
                  Share your passes with customers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-6 border rounded-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <QrCode className="w-8 h-8 text-blue-600" />
                      <div>
                        <h3 className="font-semibold">QR Code</h3>
                        <p className="text-sm text-gray-600">Generate QR codes for easy sharing</p>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full" disabled>
                      Generate QR Code
                    </Button>
                  </div>

                  <div className="p-6 border rounded-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <Download className="w-8 h-8 text-green-600" />
                      <div>
                        <h3 className="font-semibold">Direct Download</h3>
                        <p className="text-sm text-gray-600">Provide direct download links</p>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full" disabled>
                      Get Download Link
                    </Button>
                  </div>
                </div>

                <Separator className="my-6" />

                <div className="text-center text-gray-600">
                  <p>Distribution features will be available after you build your first pass.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PassBuilderPage;